import React, { useState, useEffect, useRef } from 'react';
import {
  Form, Row, Col, Card, Button, Badge, Modal, ListGroup,
  InputGroup, Table, Spinner,
} from 'react-bootstrap';
import api from '../../services/api';
import { useInterfaceStore } from '../../stores/useInterfaceStore';
import { notify } from '../../stores/useNotificationStore';
import { FiSearch, FiX, FiPlus, FiTrash2, FiPaperclip, FiLink, FiSave, FiAlertTriangle } from '../../utils/icons';

// ── Types ──────────────────────────────────────────────────────────────────────

type TipoResposta = 'SIM_NAO' | 'PERCENTUAL';
type NivelControle = 'MANDATORIO' | 'RECOMENDADO' | 'DESEJAVEL';

const NIVEL_CONTROLE_OPTIONS: { value: NivelControle; label: string }[] = [
  { value: 'MANDATORIO', label: 'Mandatório' },
  { value: 'RECOMENDADO', label: 'Recomendado' },
  { value: 'DESEJAVEL', label: 'Desejável' },
];

interface ChecklistItem {
  id?: number;
  descricao: string;
  percentual_peso: number;
  ativo: boolean;
  nivel_controle: NivelControle;
}

interface DocumentoItem {
  tipo_documento: string;
  documento: string;
  arquivo_key?: string | null;
  arquivo_nome?: string | null;
}

const TIPO_DOCUMENTO_OPTIONS = [
  'Política', 'Norma', 'Procedimento', 'Instrução de Trabalho', 'Contrato', 'Relatório', 'Outro',
];

interface Framework { id: number; nome: string; descricao?: string; }
interface Dominio   { id: number; nome: string; framework_id: number; indice?: string; parent_id?: number | null; }
interface PerguntaSimples { id: number; pergunta: string; framework_nome?: string; dominio_nome?: string; }

export interface QuestionFormData {
  pergunta: string;
  dominio_id: number | null;
  framework_id: number | null;
  tipo_resposta: TipoResposta;
  permite_na: boolean;
  usa_nivel_controle: boolean;
  nivel_controle: NivelControle | null;
  peso: number;
  checklist_data: { itens: ChecklistItem[]; remove?: boolean; peso_automatico?: boolean } | null;
  documentos: DocumentoItem[];
  is_modelo: boolean;
  empresa_id?: number;
}

interface QuestionFormProps {
  onSubmit: (data: QuestionFormData, equivalencias: number[]) => void;
  questionToEdit?: any | null;
  isLoading?: boolean;
}

// ── Predictive Search ─────────────────────────────────────────────────────────

interface PredictiveProps {
  items: Framework[];
  value: string;
  placeholder?: string;
  onSelect: (item: Framework) => void;
  onClear?: () => void;
  disabled?: boolean;
}

const FrameworkSearch: React.FC<PredictiveProps> = ({ items, value, placeholder, onSelect, onClear, disabled }) => {
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => { setText(value); }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = text.trim().length >= 1
    ? items.filter(i => i.nome.toLowerCase().includes(text.toLowerCase()))
    : items.slice(0, 20);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <InputGroup>
        <Form.Control
          value={text}
          onChange={e => { setText(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder || 'Pesquisar framework...'}
          disabled={disabled}
        />
        {onClear && value && (
          <Button variant="outline-secondary" size="sm" onClick={() => { setText(''); onClear(); }}><FiX /></Button>
        )}
      </InputGroup>
      {open && filtered.length > 0 && (
        <div style={{
          position: 'absolute', zIndex: 1050, width: '100%',
          background: '#fff', border: '1px solid #dee2e6',
          borderRadius: '0 0 4px 4px', maxHeight: 200, overflowY: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,.15)',
        }}>
          {filtered.map(item => (
            <div
              key={item.id}
              style={{ padding: '8px 12px', cursor: 'pointer' }}
              onMouseDown={e => e.preventDefault()}
              onClick={() => { onSelect(item); setText(item.nome); setOpen(false); }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f8f9fa')}
              onMouseLeave={e => (e.currentTarget.style.background = '')}
            >
              <div className="fw-semibold">{item.nome}</div>
              {item.descricao && <div className="text-muted small">{item.descricao}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Equivalence modal ─────────────────────────────────────────────────────────

interface EquivModalProps {
  show: boolean;
  onHide: () => void;
  perguntas: PerguntaSimples[];
  selected: number[];
  onToggle: (id: number) => void;
}

const EquivModal: React.FC<EquivModalProps> = ({ show, onHide, perguntas, selected, onToggle }) => {
  const [search, setSearch] = useState('');
  const filtered = perguntas.filter(p =>
    p.pergunta.toLowerCase().includes(search.toLowerCase()) ||
    (p.framework_nome || '').toLowerCase().includes(search.toLowerCase())
  );
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton><Modal.Title>Relação de Equivalência</Modal.Title></Modal.Header>
      <Modal.Body>
        <Form.Control
          className="mb-3" autoFocus
          placeholder="Pesquisar pergunta..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <ListGroup style={{ maxHeight: 420, overflowY: 'auto' }}>
          {filtered.length === 0 && <ListGroup.Item className="text-muted">Nenhuma pergunta encontrada</ListGroup.Item>}
          {filtered.map(p => (
            <ListGroup.Item
              key={p.id}
              action
              active={selected.includes(p.id)}
              onClick={() => onToggle(p.id)}
              className="d-flex justify-content-between align-items-center"
            >
              <div>
                <div style={{ fontSize: '0.9rem' }}>{p.pergunta}</div>
                {p.framework_nome && (
                  <small className="text-muted">{p.framework_nome}</small>
                )}
              </div>
              {selected.includes(p.id) && <Badge bg="success">✓</Badge>}
            </ListGroup.Item>
          ))}
        </ListGroup>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}><FiX /> Fechar</Button>
      </Modal.Footer>
    </Modal>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────

const QuestionForm: React.FC<QuestionFormProps> = ({ onSubmit, questionToEdit, isLoading }) => {
  const { empresaId } = useInterfaceStore();

  // Core fields
  const [pergunta, setPergunta] = useState('');
  const [frameworkId, setFrameworkId] = useState<number | null>(null);
  const [frameworkNome, setFrameworkNome] = useState('');
  const [dominioId, setDominioId] = useState<number | null>(null);
  const [tipoResposta, setTipoResposta] = useState<TipoResposta>('SIM_NAO');
  const [permiteNA, setPermiteNA] = useState(true);
  // Nível de Controle (atributo da pergunta): quando ligado, escolhe-se Mandatório/Recomendado/Desejável.
  const [usaNivelControle, setUsaNivelControle] = useState(false);
  const [nivelControle, setNivelControle] = useState<NivelControle>('MANDATORIO');

  // Checklist
  const [hasChecklist, setHasChecklist] = useState(false);
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [newItemDesc, setNewItemDesc] = useState('');
  const [newItemPeso, setNewItemPeso] = useState(0);
  const [newItemNivel, setNewItemNivel] = useState<NivelControle>('MANDATORIO');
  const [itemsPesoAuto, setItemsPesoAuto] = useState(true);

  // Peso
  const [peso, setPeso] = useState(100);

  // Document
  // Documentos (0..N): lista + campos da linha de adição.
  const [documentos, setDocumentos] = useState<DocumentoItem[]>([]);
  const [newDocTipo, setNewDocTipo] = useState('');
  const [newDocTexto, setNewDocTexto] = useState('');
  const [newDocArquivoKey, setNewDocArquivoKey] = useState<string | null>(null);
  const [newDocArquivoNome, setNewDocArquivoNome] = useState<string | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Equivalencias
  const [equivalencias, setEquivalencias] = useState<number[]>([]);

  // Reference data
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [dominios, setDominios] = useState<Dominio[]>([]);
  const [todasPerguntas, setTodasPerguntas] = useState<PerguntaSimples[]>([]);

  // Modal state
  const [showFwModal, setShowFwModal] = useState(false);
  const [fwSearch, setFwSearch] = useState('');
  const [showEquivModal, setShowEquivModal] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  // Pop-up de erro do Checklist ao tentar salvar (soma de peso > 100% ou nenhum item
  // adicionado) — guarda a mensagem a exibir; null = fechado. Mais assertivo que só o
  // texto vermelho no topo da seção, que passa despercebido se ela estiver fora da tela.
  const [checklistErrorModal, setChecklistErrorModal] = useState<string | null>(null);
  const [checklistPulse, setChecklistPulse] = useState(false);
  const checklistCardRef = useRef<HTMLDivElement>(null);

  // ── Load reference data ───────────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      const [fwRes, qRes] = await Promise.all([
        api.getFrameworks(),
        api.getQuestions(),
      ]);
      if (fwRes.success) setFrameworks(fwRes.data || []);
      if (qRes.success) setTodasPerguntas((qRes.data || []) as PerguntaSimples[]);
    };
    load();
  }, []);

  // Load dominios when framework changes
  useEffect(() => {
    if (!frameworkId) { setDominios([]); setDominioId(null); return; }
    api.getDominiosByFramework(frameworkId).then(res => {
      if (res.success) {
        const list: Dominio[] = (res.data || []) as Dominio[];
        const sorted = list.sort((a, b) => (a.indice || '').localeCompare(b.indice || '', undefined, { numeric: true }));
        setDominios(sorted);
        // Mantém o domínio já selecionado se ele pertencer à lista recém-carregada (caso
        // de edição, onde framework e domínio são hidratados juntos a partir do registro
        // salvo); só zera quando o domínio anterior não existe mais nessa lista (troca
        // manual de framework pelo usuário). Zerar aqui incondicionalmente, antes do fetch
        // resolver, apagava o domínio salvo ao abrir a edição de uma pergunta existente.
        setDominioId(prev => (prev != null && sorted.some(d => d.id === prev)) ? prev : null);
      }
    });
  }, [frameworkId]);

  // ── Populate on edit ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!questionToEdit) return;
    setPergunta(questionToEdit.pergunta || '');
    setFrameworkId(questionToEdit.framework_id || null);
    setFrameworkNome(questionToEdit.framework_nome || '');
    setDominioId(questionToEdit.dominio_id || null);
    setTipoResposta(questionToEdit.tipo_resposta === 'PERCENTUAL' ? 'PERCENTUAL' : 'SIM_NAO');
    setPermiteNA(questionToEdit.permite_na !== false);
    setUsaNivelControle(!!questionToEdit.usa_nivel_controle);
    setNivelControle((questionToEdit.nivel_controle as NivelControle) || 'MANDATORIO');
    // Documentos: usa a lista nova; se vier só o legado, converte para 1 item.
    if (Array.isArray(questionToEdit.documentos)) {
      setDocumentos(questionToEdit.documentos.map((d: any) => ({
        tipo_documento: d.tipo_documento || '',
        documento: d.documento || '',
        arquivo_key: d.arquivo_key || null,
        arquivo_nome: d.arquivo_nome || null,
      })));
    } else if (questionToEdit.tipo_documento || questionToEdit.documento) {
      setDocumentos([{ tipo_documento: questionToEdit.tipo_documento || '', documento: questionToEdit.documento || '' }]);
    }
    setPeso(questionToEdit.peso ?? 100);
    // Checklist
    if (questionToEdit.checklist && questionToEdit.checklist.itens && questionToEdit.checklist.itens.length > 0) {
      setHasChecklist(true);
      setChecklistItems(questionToEdit.checklist.itens.map((it: any) => ({
        id: it.id,
        descricao: it.descricao_item || it.descricao,
        percentual_peso: it.percentual_peso || 0,
        ativo: it.ativo !== false,
        nivel_controle: (it.nivel_controle as NivelControle) || 'MANDATORIO',
      })));
      // Restaura o modo (automático/manual) como estava quando foi salvo — antes disso
      // sempre forçava desligado ao abrir para edição, mesmo quando o peso tinha sido
      // definido por cálculo automático e nunca ajustado manualmente (SM-002).
      setItemsPesoAuto(questionToEdit.checklist.peso_automatico !== false);
    }
    // Equivalencias
    const equivIds = (questionToEdit.equivalencias || []).map((eq: any) => eq.pergunta_id_2).filter(Boolean);
    setEquivalencias(equivIds);
  }, [questionToEdit]);

  // ── Auto peso dos itens do checklist ───────────────────────────────────────
  // Quando ligado: lê o Peso da pergunta, calcula a diferença para 100 e divide
  // igualmente entre os itens ativos (que entram no cálculo de maturidade).

  const ativoSignature = checklistItems.map(i => (i.ativo ? '1' : '0')).join('');

  // Soma ao vivo (recalculada a cada render): permite avisar o usuário assim que o
  // limite de 100% é ultrapassado, em vez de só no clique em Salvar.
  // Number(...): percentual_peso pode vir como string do backend (coluna numérica do
  // Postgres) — sem essa coerção, "0 + '20.00'" vira concatenação de texto, não soma.
  const somaItensAtivos = checklistItems.filter(i => i.ativo).reduce((s, i) => s + (Number(i.percentual_peso) || 0), 0);
  const somaTotalPeso = peso + somaItensAtivos;
  const excedeLimitePeso = hasChecklist && !itemsPesoAuto && somaTotalPeso > 100;
  // SM-003: o peso precisa fechar em EXATAMENTE 100%, não só não passar disso. Sem
  // checklist, o campo Peso já é travado em 0–100 na digitação (não dá pra passar de
  // 100), então só falta checar o mínimo; com checklist em modo manual, falta os dois —
  // o de cima (excedeLimitePeso) já existia, esse aqui cobre a metade que faltava.
  const faltaPeso = hasChecklist ? (!itemsPesoAuto && somaTotalPeso < 100) : peso < 100;

  useEffect(() => {
    if (!itemsPesoAuto) return;
    const ativos = checklistItems.filter(i => i.ativo);
    if (ativos.length === 0) return;
    const pesoPorItem = Math.max(0, 100 - peso) / ativos.length;
    setChecklistItems(prev => prev.map(i => (
      i.ativo ? { ...i, percentual_peso: Math.round(pesoPorItem * 100) / 100 } : i
    )));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsPesoAuto, peso, checklistItems.length, ativoSignature]);

  // ── Checklist management ──────────────────────────────────────────────────

  const handleAddItem = () => {
    if (!newItemDesc.trim()) return;
    setChecklistItems(prev => [...prev, {
      descricao: newItemDesc.trim(), percentual_peso: newItemPeso, ativo: true, nivel_controle: newItemNivel,
    }]);
    setNewItemDesc('');
    setNewItemPeso(0);
  };

  const handleRemoveItem = (idx: number) => {
    setChecklistItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleItemChange = (idx: number, field: keyof ChecklistItem, value: any) => {
    setChecklistItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      // Nível de Maturidade "Não": o item não entra no cálculo, então o peso é zerado.
      if (field === 'ativo' && value === false) updated.percentual_peso = 0;
      return updated;
    }));
  };

  // ── Documentos (0..N) ─────────────────────────────────────────────────────

  // Faz o upload imediato do arquivo anexo e guarda a chave para o item em edição.
  const handleUploadDoc = async (file: File | null) => {
    if (!file) return;
    setUploadingDoc(true);
    try {
      const res = await api.uploadPerguntaDocumento(file);
      if (res.success && res.data) {
        setNewDocArquivoKey(res.data.arquivo_key);
        setNewDocArquivoNome(res.data.arquivo_nome);
      } else {
        notify.error((res.error as string) || 'Erro ao enviar o arquivo.');
      }
    } catch {
      notify.error('Erro de conexão ao enviar o arquivo.');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleAddDoc = () => {
    if (!newDocTipo.trim() && !newDocTexto.trim() && !newDocArquivoKey) return;
    setDocumentos(prev => [...prev, {
      tipo_documento: newDocTipo.trim(),
      documento: newDocTexto.trim(),
      arquivo_key: newDocArquivoKey,
      arquivo_nome: newDocArquivoNome,
    }]);
    setNewDocTipo('');
    setNewDocTexto('');
    setNewDocArquivoKey(null);
    setNewDocArquivoNome(null);
  };

  const handleRemoveDoc = (idx: number) => {
    setDocumentos(prev => prev.filter((_, i) => i !== idx));
  };

  // Abre o arquivo anexo em nova aba (URL assinada gerada sob demanda).
  const handleDownloadDoc = async (key: string) => {
    try {
      const res = await api.getPerguntaDocumentoUrl(key);
      if (res.success && res.data?.url) window.open(res.data.url, '_blank', 'noopener');
      else notify.error('Não foi possível abrir o arquivo.');
    } catch {
      notify.error('Erro ao abrir o arquivo.');
    }
  };

  // ── Equivalencias ─────────────────────────────────────────────────────────

  const toggleEquivalencia = (id: number) => {
    setEquivalencias(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // ── Validation & Submit ───────────────────────────────────────────────────

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!pergunta.trim()) errs.pergunta = 'Texto da pergunta é obrigatório.';
    if (!tipoResposta) errs.tipoResposta = 'Selecione o tipo de resposta.';
    if (hasChecklist && checklistItems.length === 0) errs.checklist = 'Adicione pelo menos um item ao checklist.';
    if (hasChecklist && !itemsPesoAuto) {
      const somaItens = checklistItems.filter(i => i.ativo).reduce((s, i) => s + (i.percentual_peso || 0), 0);
      const totalPeso = peso + somaItens;
      // SM-003: o peso precisa fechar em EXATAMENTE 100% — antes só bloqueava passar de
      // 100%, faltava bloquear quando fica abaixo.
      if (totalPeso > 100) {
        errs.checklist = `A soma do Peso da pergunta (${peso}%) com os pesos dos itens ativos (${somaItens.toFixed(2)}%) não pode passar de 100%.`;
      } else if (totalPeso < 100) {
        errs.checklist = `A soma do Peso da pergunta (${peso}%) com os pesos dos itens ativos (${somaItens.toFixed(2)}%) não pode ser menor que 100%.`;
      }
    } else if (!hasChecklist && peso < 100) {
      errs.checklist = 'O peso da pergunta sem checklist não pode ser menor que 100%.';
    }
    setErrors(errs);
    return errs;
  };

  // Leva o olhar do usuário direto para a seção 4 (rola até ela e pisca a borda em
  // vermelho por um instante) — chamado tanto pelo pop-up quanto, se preferir, direto.
  const focarChecklistComErro = () => {
    checklistCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setChecklistPulse(true);
    setTimeout(() => setChecklistPulse(false), 1600);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      if (errs.checklist) {
        setChecklistErrorModal(errs.checklist);
        focarChecklistComErro();
      }
      return;
    }

    // Inclui um item ainda não adicionado (digitado na linha) para não perdê-lo ao salvar.
    const documentosFinais = [...documentos];
    if (newDocTipo.trim() || newDocTexto.trim() || newDocArquivoKey) {
      documentosFinais.push({
        tipo_documento: newDocTipo.trim(),
        documento: newDocTexto.trim(),
        arquivo_key: newDocArquivoKey,
        arquivo_nome: newDocArquivoNome,
      });
    }

    const data: QuestionFormData = {
      pergunta: pergunta.trim(),
      dominio_id: dominioId,
      framework_id: frameworkId,
      tipo_resposta: tipoResposta,
      // Quando usa Nível de Controle, o N/A fica desligado (regra 2.3).
      permite_na: usaNivelControle ? false : permiteNA,
      usa_nivel_controle: usaNivelControle,
      nivel_controle: usaNivelControle ? nivelControle : null,
      peso,
      checklist_data: hasChecklist ? { itens: checklistItems, peso_automatico: itemsPesoAuto } : (questionToEdit?.checklist_id ? { itens: [], remove: true } : null),
      documentos: documentosFinais,
      is_modelo: true,
      empresa_id: empresaId || undefined,
    };

    onSubmit(data, equivalencias);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  const equivPerguntas = todasPerguntas.filter(p =>
    p.id !== questionToEdit?.id
  );

  const equivDisplay = equivalencias
    .map(id => todasPerguntas.find(p => p.id === id))
    .filter(Boolean) as PerguntaSimples[];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Form onSubmit={handleSubmit}>
      {/* ── Seção 1: Pergunta ── */}
      <Card className="mb-4">
        <Card.Header as="h6" className="fw-semibold">1. Texto da Pergunta</Card.Header>
        <Card.Body>
          <Form.Group>
            <Form.Label>Pergunta <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea" rows={3}
              value={pergunta}
              onChange={e => setPergunta(e.target.value)}
              placeholder="Digite o texto completo da pergunta..."
              isInvalid={!!errors.pergunta}
            />
            <Form.Control.Feedback type="invalid">{errors.pergunta}</Form.Control.Feedback>
          </Form.Group>
        </Card.Body>
      </Card>

      {/* ── Seção 2: Framework e Domínio ── */}
      <Card className="mb-4">
        <Card.Header as="h6" className="fw-semibold">2. Framework e Domínio de Controle</Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label>Framework</Form.Label>
              <div className="d-flex gap-2 align-items-start">
                <div className="flex-grow-1">
                  <FrameworkSearch
                    items={frameworks}
                    value={frameworkNome}
                    placeholder="Pesquisar framework..."
                    onSelect={fw => { setFrameworkId(fw.id); setFrameworkNome(fw.nome); }}
                    onClear={() => { setFrameworkId(null); setFrameworkNome(''); setDominios([]); setDominioId(null); }}
                  />
                </div>
                <Button
                  variant="outline-secondary"
                  onClick={() => { setFwSearch(''); setShowFwModal(true); }}
                  title="Ver todos os frameworks"
                  style={{ flexShrink: 0 }}
                >
                  <FiSearch />
                </Button>
              </div>
            </Col>
            <Col md={6}>
              <Form.Label>Domínio de Controle</Form.Label>
              <Form.Select
                value={dominioId ?? ''}
                onChange={e => setDominioId(e.target.value ? Number(e.target.value) : null)}
                disabled={!frameworkId}
              >
                <option value="">{frameworkId ? 'Selecione um domínio' : 'Selecione um framework primeiro'}</option>
                {dominios.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.indice ? `${d.indice} – ` : ''}{d.nome}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── Seção 3: Tipo de Resposta e Condições ── */}
      <Card className="mb-4">
        <Card.Header as="h6" className="fw-semibold">3. Tipo de Resposta e Condições</Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Label>Tipo de Resposta <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={tipoResposta}
                onChange={e => setTipoResposta(e.target.value as TipoResposta)}
                isInvalid={!!errors.tipoResposta}
              >
                <option value="SIM_NAO">Sim / Não</option>
                <option value="PERCENTUAL">Barra de Percentagem</option>
              </Form.Select>
            </Col>

            {tipoResposta === 'SIM_NAO' && (
              <Col md={4} className="d-flex align-items-end">
                <div className="text-muted small p-2 border rounded bg-light">
                  As condições (Ação quando SIM / NÃO) e o destino para perguntas complementares
                  são configurados dentro do Questionário, logo abaixo da pergunta principal.
                </div>
              </Col>
            )}

            {tipoResposta === 'PERCENTUAL' && (
              <Col md={4} className="d-flex align-items-end">
                <div className="text-muted small p-2 border rounded bg-light">
                  Para o tipo Percentual, a resposta é a própria porcentagem (0–100%).
                  O sistema vai para a próxima pergunta principal após a resposta.
                </div>
              </Col>
            )}

            {/* Usa Nível de Controle? — quando ligado, escolhe Mandatório/Recomendado/Desejável
                e o N/A (Não Aplicável) é automaticamente desligado (regra 2.3). */}
            <Col md={2} className="d-flex align-items-end">
              <Form.Check
                type="switch"
                id="usa-nivel-controle-switch"
                checked={usaNivelControle}
                onChange={e => { setUsaNivelControle(e.target.checked); if (e.target.checked) setPermiteNA(false); }}
                label={<span>Usa Nível de Controle? <span className="text-muted">— {usaNivelControle ? 'Ligado' : 'Desligado'}</span></span>}
              />
            </Col>

            <Col md={2} className="d-flex align-items-end">
              {usaNivelControle ? (
                <div className="border rounded p-2 w-100">
                  {NIVEL_CONTROLE_OPTIONS.map(o => (
                    <Form.Check
                      key={o.value}
                      type="radio"
                      name="nivel-controle-pergunta"
                      id={`nivel-${o.value}`}
                      label={o.label}
                      checked={nivelControle === o.value}
                      onChange={() => setNivelControle(o.value)}
                    />
                  ))}
                </div>
              ) : (
                <Form.Check
                  type="switch"
                  id="permite-na-switch"
                  checked={permiteNA}
                  disabled={usaNivelControle}
                  onChange={e => setPermiteNA(e.target.checked)}
                  label={<span>NA (Não Aplicável) <span className="text-muted">— {permiteNA ? 'Ligado' : 'Desligado'}</span></span>}
                />
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── Seção 4: Checklist ── */}
      <Card
        className={`mb-4 ${checklistPulse ? 'border-danger' : ''}`}
        style={checklistPulse ? { boxShadow: '0 0 0 0.2rem rgba(220, 53, 69, 0.35)', transition: 'box-shadow 0.2s ease' } : undefined}
        ref={checklistCardRef}
      >
        <Card.Header as="h6" className="fw-semibold d-flex align-items-center gap-3">
          4. Checklist
          <Form.Check
            type="switch"
            checked={hasChecklist}
            onChange={e => { setHasChecklist(e.target.checked); if (!e.target.checked) setChecklistItems([]); }}
            label={hasChecklist ? 'Ligado' : 'Desligado'}
            className="ms-2 mb-0"
          />
        </Card.Header>
        {hasChecklist && (
          <Card.Body>
            {errors.checklist && <div className="text-danger small mb-3">{errors.checklist}</div>}

            {/* Add new item */}
            <Row className="g-2 mb-3 align-items-end">
              <Col md={5}>
                <Form.Label className="small">Item do Checklist</Form.Label>
                <Form.Control
                  value={newItemDesc}
                  onChange={e => setNewItemDesc(e.target.value)}
                  placeholder="Descrição do item..."
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddItem(); } }}
                />
              </Col>
              <Col md={3}>
                <Form.Label className="small">Nível do Controle</Form.Label>
                <Form.Select
                  value={newItemNivel}
                  onChange={e => setNewItemNivel(e.target.value as NivelControle)}
                >
                  {NIVEL_CONTROLE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Label className="small">Percentual Peso</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="number" min={0} max={100} step={0.01}
                    value={newItemPeso}
                    onChange={e => setNewItemPeso(parseFloat(e.target.value) || 0)}
                    disabled={itemsPesoAuto}
                  />
                  <InputGroup.Text>%</InputGroup.Text>
                </InputGroup>
              </Col>
              <Col md={2}>
                <Button variant="outline-primary" className="w-100" onClick={handleAddItem} disabled={!newItemDesc.trim()}>
                  <FiPlus /> Adicionar
                </Button>
              </Col>
            </Row>

            {/* Items table */}
            {checklistItems.length > 0 && (
              <Table size="sm" bordered>
                  <thead className="table-light">
                    <tr>
                      <th colSpan={2} className="text-center">Nível de Maturidade</th>
                      <th rowSpan={2} className="align-middle" style={{ width: 160 }}>Nível do Controle</th>
                      <th rowSpan={2} className="align-middle">Item</th>
                      <th rowSpan={2} className="align-middle" style={{ width: 120 }}>Peso (%)</th>
                      <th rowSpan={2} style={{ width: 60 }}></th>
                    </tr>
                    <tr>
                      <th className="text-center" style={{ width: 55 }}>Sim</th>
                      <th className="text-center" style={{ width: 55 }}>Não</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checklistItems.map((item, idx) => (
                      <tr key={idx}>
                        <td className="text-center align-middle">
                          <Form.Check
                            type="radio"
                            name={`item-ativo-${idx}`}
                            checked={item.ativo}
                            onChange={() => handleItemChange(idx, 'ativo', true)}
                          />
                        </td>
                        <td className="text-center align-middle">
                          <Form.Check
                            type="radio"
                            name={`item-ativo-${idx}`}
                            checked={!item.ativo}
                            onChange={() => handleItemChange(idx, 'ativo', false)}
                          />
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            value={item.nivel_controle}
                            onChange={e => handleItemChange(idx, 'nivel_controle', e.target.value as NivelControle)}
                          >
                            {NIVEL_CONTROLE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Control
                            size="sm"
                            value={item.descricao}
                            onChange={e => handleItemChange(idx, 'descricao', e.target.value)}
                          />
                        </td>
                        <td>
                          <InputGroup size="sm">
                            <Form.Control
                              type="number" min={0} max={100} step={0.01}
                              // Nível de Maturidade "Não": caixa vazia e desabilitada para edição.
                              value={item.ativo ? item.percentual_peso : ''}
                              onChange={e => handleItemChange(idx, 'percentual_peso', parseFloat(e.target.value) || 0)}
                              disabled={itemsPesoAuto || !item.ativo}
                            />
                            <InputGroup.Text>%</InputGroup.Text>
                          </InputGroup>
                        </td>
                        <td className="text-center">
                          <Button size="sm" variant="outline-danger" onClick={() => handleRemoveItem(idx)}><FiX /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
            )}

            {/* Sempre visível, mesmo antes de existir o primeiro item — o campo "Percentual
                Peso" acima já nasce desabilitado quando este toggle está ligado (padrão),
                então ele precisa estar à vista para explicar/permitir desligar isso. */}
            <div className="d-flex justify-content-end">
              <Form.Check
                type="switch"
                label="Calcular automaticamente"
                checked={itemsPesoAuto}
                onChange={e => setItemsPesoAuto(e.target.checked)}
              />
            </div>
            <div className={`small mt-1 ${(excedeLimitePeso || faltaPeso) ? 'text-danger fw-semibold' : 'text-muted'}`}>
              {itemsPesoAuto
                ? `Peso da pergunta: ${peso}% + itens dividem os ${Math.max(0, 100 - peso)}% restantes igualmente entre os que estiverem "Sim".`
                : `Informe o peso de cada item manualmente. Soma atual: Peso da pergunta (${peso}%) + itens marcados "Sim" (${somaItensAtivos.toFixed(2)}%) = ${somaTotalPeso.toFixed(2)}%${
                    excedeLimitePeso ? ' — excede 100%, reduza algum valor antes de salvar.'
                    : faltaPeso ? ' — menor que 100%, aumente algum valor antes de salvar.'
                    : '.'
                  }`}
            </div>
          </Card.Body>
        )}
      </Card>

      {/* ── Seção 5: Peso ── */}
      <Card className="mb-4">
        <Card.Header as="h6" className="fw-semibold">5. Peso</Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Label>Peso (0–100)</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number" min={0} max={100}
                  value={peso}
                  onChange={e => setPeso(Math.min(100, Math.max(0, Number(e.target.value))))}
                />
                <InputGroup.Text>%</InputGroup.Text>
              </InputGroup>
            </Col>
            <Col md={8}>
              <div className={`p-2 border rounded small ${(excedeLimitePeso || faltaPeso) ? 'bg-danger bg-opacity-10 text-danger fw-semibold' : 'bg-light text-muted'}`}>
                {!hasChecklist
                  ? (faltaPeso
                      ? 'O peso da pergunta sem checklist não pode ser menor que 100%.'
                      : 'Peso desta pergunta no cálculo de maturidade geral do questionário.')
                  : itemsPesoAuto
                  ? 'O restante até 100% é distribuído entre os itens do Checklist (seção 4).'
                  : `Soma atual com os itens do Checklist (seção 4): ${somaTotalPeso.toFixed(2)}%${
                      excedeLimitePeso ? ' — excede 100%.' : faltaPeso ? ' — menor que 100%.' : ' de 100%.'
                    }`}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── Seção 6: Documentos (0..N) ── */}
      <Card className="mb-4">
        <Card.Header as="h6" className="fw-semibold">6. Tipo de Documento</Card.Header>
        <Card.Body>
          {/* Linha de adição */}
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Label>Tipo de Documento</Form.Label>
              <Form.Select value={newDocTipo} onChange={e => setNewDocTipo(e.target.value)}>
                <option value="">Selecione...</option>
                {TIPO_DOCUMENTO_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>Documento <small className="text-muted">(nome ou link)</small></Form.Label>
              <Form.Control
                value={newDocTexto}
                onChange={e => setNewDocTexto(e.target.value)}
                placeholder="Ex: PNP-001 — Política de Segurança ou https://..."
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddDoc(); } }}
              />
            </Col>
            <Col md={3}>
              <Form.Label>Anexar arquivo <small className="text-muted">(opcional)</small></Form.Label>
              <Form.Control
                type="file"
                size="sm"
                disabled={uploadingDoc}
                onChange={e => {
                  const f = (e.target as HTMLInputElement).files?.[0] || null;
                  e.target.value = ''; // permite reanexar o mesmo arquivo
                  handleUploadDoc(f);
                }}
              />
              <div className="small mt-1">
                {uploadingDoc
                  ? <span className="text-muted"><Spinner as="span" size="sm" animation="border" className="me-1" />Enviando...</span>
                  : newDocArquivoNome
                    ? <span className="text-success">✓ {newDocArquivoNome}</span>
                    : null}
              </div>
            </Col>
            <Col md={2}>
              <Button
                variant="outline-primary"
                className="w-100"
                onClick={handleAddDoc}
                disabled={uploadingDoc || (!newDocTipo.trim() && !newDocTexto.trim() && !newDocArquivoKey)}
              >
                <FiPlus /> Adicionar Item
              </Button>
            </Col>
          </Row>

          {/* Grid dos documentos cadastrados */}
          {documentos.length > 0 && (
            <div className="mt-3">
              <div className="fw-semibold small text-muted mb-1">Documentos Cadastrados</div>
              <Table size="sm" bordered className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 50 }}></th>
                    <th style={{ width: '25%' }}>Tipo de Documento</th>
                    <th>Documento</th>
                    <th style={{ width: '25%' }}>Arquivo</th>
                  </tr>
                </thead>
                <tbody>
                  {documentos.map((doc, idx) => (
                    <tr key={idx}>
                      <td className="text-center align-middle">
                        <Button
                          variant="link"
                          className="text-danger p-0"
                          title="Excluir"
                          onClick={() => handleRemoveDoc(idx)}
                        >
                          <FiTrash2 />
                        </Button>
                      </td>
                      <td className="align-middle">{doc.tipo_documento || <span className="text-muted">—</span>}</td>
                      <td className="align-middle">{doc.documento || <span className="text-muted">—</span>}</td>
                      <td className="align-middle">
                        {doc.arquivo_key
                          ? (
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-decoration-none"
                              onClick={() => handleDownloadDoc(doc.arquivo_key as string)}
                            >
                              <FiPaperclip /> {doc.arquivo_nome || 'arquivo'}
                            </Button>
                          )
                          : <span className="text-muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ── Seção 7: Equivalências ── */}
      <Card className="mb-4">
        <Card.Header as="h6" className="fw-semibold">7. Relação de Equivalência</Card.Header>
        <Card.Body>
          <p className="text-muted small mb-3">
            Relacione esta pergunta com perguntas equivalentes de outros frameworks.
            Ao responder uma, a equivalente recebe automaticamente a mesma resposta.
          </p>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowEquivModal(true)}
          >
            <FiLink /> Selecionar Perguntas Equivalentes ({equivalencias.length})
          </Button>

          {equivDisplay.length > 0 && (
            <div className="mt-3">
              {equivDisplay.map(p => (
                <div
                  key={p.id}
                  className="d-flex align-items-center gap-2 mb-2 p-2 border rounded"
                >
                  <div className="flex-grow-1">
                    <div className="small">{p.pergunta}</div>
                    {p.framework_nome && <small className="text-muted">{p.framework_nome}</small>}
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => toggleEquivalencia(p.id)}
                  >
                    <FiX />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ── Action buttons ── */}
      <div className="d-flex gap-2 justify-content-end mb-4">
        <Button variant="primary" type="submit" disabled={isLoading}>
          {isLoading
            ? <><Spinner as="span" size="sm" animation="border" className="me-2" />Salvando...</>
            : <><FiSave /> {questionToEdit ? 'Salvar Alterações' : 'Criar Pergunta'}</>
          }
        </Button>
      </div>

      {/* ── Framework modal ── */}
      <Modal show={showFwModal} onHide={() => setShowFwModal(false)} size="lg" centered>
        <Modal.Header closeButton><Modal.Title>Selecionar Framework</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Control
            className="mb-3" autoFocus
            placeholder="Filtrar frameworks..."
            value={fwSearch}
            onChange={e => setFwSearch(e.target.value)}
          />
          <ListGroup style={{ maxHeight: 400, overflowY: 'auto' }}>
            {frameworks
              .filter(fw => fw.nome.toLowerCase().includes(fwSearch.toLowerCase()))
              .map(fw => (
                <ListGroup.Item
                  key={fw.id}
                  action
                  active={fw.id === frameworkId}
                  onClick={() => { setFrameworkId(fw.id); setFrameworkNome(fw.nome); setShowFwModal(false); }}
                >
                  <div className="fw-semibold">{fw.nome}</div>
                  {fw.descricao && <div className="text-muted small">{fw.descricao}</div>}
                </ListGroup.Item>
              ))}
          </ListGroup>
        </Modal.Body>
      </Modal>

      {/* ── Equivalencia modal ── */}
      <EquivModal
        show={showEquivModal}
        onHide={() => setShowEquivModal(false)}
        perguntas={equivPerguntas}
        selected={equivalencias}
        onToggle={toggleEquivalencia}
      />

      {/* ── Pop-up de erro do Checklist ao tentar salvar ── */}
      <Modal show={!!checklistErrorModal} onHide={() => setChecklistErrorModal(null)} centered>
        <Modal.Body className="p-4">
          <div className="d-flex gap-3">
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0 rounded-circle bg-danger bg-opacity-10 text-danger"
              style={{ width: 48, height: 48 }}
            >
              <FiAlertTriangle size={24} />
            </div>
            <div>
              <div className="fw-semibold fs-6 mb-1">Não foi possível salvar a pergunta</div>
              <div className="text-muted">{checklistErrorModal}</div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top-0 pt-0">
          <Button variant="outline-secondary" size="sm" onClick={() => setChecklistErrorModal(null)}>
            Fechar
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => { setChecklistErrorModal(null); focarChecklistComErro(); }}
          >
            Ir para o Checklist
          </Button>
        </Modal.Footer>
      </Modal>
    </Form>
  );
};

export default QuestionForm;
